import { defineStore } from 'pinia';
import { useForm } from '@inertiajs/vue3';

import { Notify } from 'quasar';

import axios from 'axios';

axios.defaults.baseURL = import.meta.env.AXIOS_BASE_URL;
axios.defaults.withCredentials = true;
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

export const useNotificationStore = defineStore('notificationStore', {
    state: () => ({
        loaded: false,
        loading: false,
        data: [],
        count: 0,
        depth: 1,
        pagination: useForm({
            rowsPerPage: 10,
            cursor: null,
        }),
        cursor: {
            prev: null,
            next: null,
        },
        filters: {
            dateFrom: null,
            dateTo: null,
        },
    }),
    actions: {
        async fetch(cursor = null, append = false, forceFetch = false) {
            if (cursor) {
                this.pagination.cursor = cursor;
            } else if (this.loaded && this.count > 0 && !forceFetch) {
                return;
            }

            this.loading = true;

            const { data } = await axios.get(route('api.v1.notifications.index'), {
                params: {
                    ...this.pagination.data(),
                    date_from: this.filters.dateFrom,
                    date_to: this.filters.dateTo,
                },
            });
            
            if (append) {
                this.data = this.data.concat(data.data.rows);
            } else {
                this.data = data.data.rows;
            }

            this.cursor.next = data.data.cursor.next;
            this.cursor.prev = data.data.cursor.prev;

            this.count = data.data.count;

            this.loaded = true;
            this.loading = false;
        },
        async fetchNext(append = false) {
            await this.fetch(this.cursor.next, append);

            this.depth++;
        },
        async fetchPrev(append = false) {
            await this.fetch(this.cursor.prev, append);

            this.depth--;
        },
        async fetchCount() {
            const { data } = await axios.get(route('api.v1.notifications.count'));

            if (!data.success) {
                console.error(data);
                return;
            }

            this.count = data.count;
        },
        async markAsRead(id) {
            const { data } = await axios.put(route('api.v1.notifications.read', id));

            if (!data.success) {
                console.error(data);
                return;
            }

            this.data = this.data.filter((notification) => notification.id !== id);
            // await this.fetch();
        },
        async markAllAsRead() {
            const { data } = await axios.put(route('api.v1.notifications.read.all'));

            if (!data.success) {
                console.error(data);

                Notify.create({
                    message: data.message ?? 'An error occurred',
                    color: 'negative',
                    icon: 'mdi-thumb-down'
                });

                return;
            }

            Notify.create({
                message: data.message ?? 'All notifications marked as read',
                color: 'positive',
                icon: 'mdi-thumb-up'
            });

            await this.fetch(null, false, true);
        },
        setDateFilter(dateFrom, dateTo) {
            this.filters.dateFrom = dateFrom;
            this.filters.dateTo = dateTo;
            this.depth = 1;
            this.fetch(null, false, true);
        },
        clearFilters() {
            this.filters.dateFrom = null;
            this.filters.dateTo = null;
            this.depth = 1;
            this.fetch(null, false, true);
        },
        exportCSV() {
            const params = new URLSearchParams();
            if (this.filters.dateFrom) params.append('date_from', this.filters.dateFrom);
            if (this.filters.dateTo) params.append('date_to', this.filters.dateTo);

            window.open(
                `/api/v1/notifications/export?${params.toString()}`,
                '_blank'
            );
        },
    },
    getters: {
        paginationData() {
            return this.pagination.data();
        },
        loadMoreText() {
            if (this.loading) {
                return 'Loading...';
            }

            if (!this.cursor.next) {
                return 'No more notifications';
            }
            
            const count = this.count - this.data.length;

            return `Load ${count > this.pagination.rowsPerPage ? this.pagination.rowsPerPage : count } more`;
        }
    },
});